import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faLayerGroup, faListCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { notifications } from '@mantine/notifications';
import type { ItemsReportOptionItem, ItemsReportRow, ItemsReportView } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import { reportsService } from '../../../../../services/reports/reports.service';
import { productsService, type ProductView } from '../../../../../services/products/products.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import { formatMoney, isolateAmountLtr, roundMoney } from '../../../../../utils/money';
import { PosItemsProductPickerModal } from './PosItemsProductPickerModal';
import {
  ITEMS_REPORT_PRESETS,
  formatRangeLabel,
  rangeForPreset,
  type ItemsReportPreset,
} from './items-report-range.util';
import classes from './pos-items-report.module.scss';

export type PosItemsReportModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
};

type Scope = 'all' | 'specific';

const Money: React.FC<{ value: number }> = ({ value }) => <span>{isolateAmountLtr(formatMoney(value))} ₪</span>;

// Mobile card cell: the table's column name paired with its value.
const Cell: React.FC<{ label: string; value: React.ReactNode; tone?: 'danger' }> = ({ label, value, tone }) => (
  <div className={classes.cardCell}>
    <span className={classes.cardCellLabel}>{label}</span>
    <strong className={tone === 'danger' ? `${classes.cardCellValue} ${classes.canceledCell}` : classes.cardCellValue}>
      {value}
    </strong>
  </div>
);

// Summary: דוח פריטים — date range + all/specific products, then a per-product sales table.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
const PosItemsReportModalComponent: React.FC<PosItemsReportModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
}) => {
  const [preset, setPreset] = useState<ItemsReportPreset>('today');
  const [range, setRange] = useState(() => rangeForPreset('today'));
  const [scope, setScope] = useState<Scope>('all');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedOptionItemIds, setSelectedOptionItemIds] = useState<number[]>([]);
  const [products, setProducts] = useState<ProductView[]>([]);
  const [optionItems, setOptionItems] = useState<ItemsReportOptionItem[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [report, setReport] = useState<ItemsReportView | null>(null);
  const [loading, setLoading] = useState(false);
  // The auto-run on open must not re-fire when filters change, so it goes through a ref.
  const runReportRef = useRef<(override?: { preset: ItemsReportPreset }) => Promise<void>>(async () => undefined);

  useEffect(() => {
    if (!opened) {
      return;
    }
    setPreset('today');
    setRange(rangeForPreset('today'));
    setScope('all');
    setSelectedIds([]);
    setSelectedOptionItemIds([]);
    setReport(null);
    setLoading(false);
    void productsService
      .listForSettings(tenantId, employeeSession)
      .then((rows) => setProducts(rows.filter((product) => product.isActive)))
      .catch(() => setProducts([]));
    void reportsService
      .listOptionItems(tenantId, employeeSession)
      .then(setOptionItems)
      .catch(() => setOptionItems([]));
    // Opening the tile lands straight on היום / כל הפריטים rather than an empty shell.
    void runReportRef.current({ preset: 'today' });
  }, [opened, tenantId, employeeSession]);

  const handlePreset = (next: ItemsReportPreset) => {
    setPreset(next);
    setReport(null);
    if (next !== 'custom') {
      setRange(rangeForPreset(next));
    }
  };

  const handleScope = (next: Scope) => {
    setScope(next);
    setReport(null);
  };

  const selectedCount = selectedIds.length + selectedOptionItemIds.length;

  const canRun = range.from !== '' && range.to !== '' && range.from <= range.to
    && (scope === 'all' || selectedCount > 0);

  const runReport = useCallback(async (override?: { preset: ItemsReportPreset }) => {
    const activePreset = override?.preset ?? preset;
    setLoading(true);
    try {
      const data = await reportsService.getItemsReport(
        tenantId,
        {
          // היום follows the live shift, not the calendar: the backend resolves the open shift,
          // today's shifts, or the last shift.
          ...(activePreset === 'today'
            ? { scope: 'today' as const }
            : { scope: 'range' as const, from: range.from, to: range.to }),
          ...(scope === 'specific' && selectedIds.length > 0 ? { productIds: selectedIds } : {}),
          ...(scope === 'specific' && selectedOptionItemIds.length > 0 ? { optionItemIds: selectedOptionItemIds } : {}),
        },
        employeeSession,
      );
      setReport(data);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'הפקת הדוח נכשלה',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [employeeSession, preset, range.from, range.to, scope, selectedOptionItemIds, selectedIds, tenantId]);

  // Totals cover the product rows only; combo children are already inside their parent's sales.
  const totals = useMemo(() => {
    if (!report || report.rows.length === 0) {
      return null;
    }
    const soldQuantity = report.rows.reduce((sum, row) => sum + row.soldQuantity, 0);
    const salesAmount = roundMoney(report.rows.reduce((sum, row) => sum + row.salesAmount, 0));
    const canceledQuantity = report.rows.reduce((sum, row) => sum + row.canceledQuantity, 0);
    return {
      soldQuantity,
      salesAmount,
      canceledQuantity,
      avgActualPrice: soldQuantity > 0 ? roundMoney(salesAmount / soldQuantity) : 0,
      salesSharePercent: report.rangeSalesTotal > 0
        ? roundMoney((salesAmount / report.rangeSalesTotal) * 100)
        : 0,
    };
  }, [report]);

  runReportRef.current = runReport;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      fullScreen
      padding={0}
      radius={0}
      zIndex={400}
      classNames={{ body: classes.modalBody }}
    >
      <div className={classes.shell} dir="rtl" data-testid="items-report-modal">
        <div className={classes.topBar}>
          <span className={classes.topTitle}>דוח פריטים</span>
          <UnstyledButton
            component="button"
            type="button"
            data-testid="items-report-close"
            className={classes.iconButton}
            onClick={onClose}
            aria-label="סגור"
          >
            <FontAwesomeIcon icon={faXmark as IconProp} />
          </UnstyledButton>
        </div>

        <div className={classes.body}>
          <div className={classes.page}>
            <section className={classes.card}>
              <div className={classes.cardLabel}>טווח תאריכים</div>
              <div className={classes.presetRow}>
                {ITEMS_REPORT_PRESETS.map((option) => (
                  <UnstyledButton
                    key={option.value}
                    component="button"
                    type="button"
                    data-testid={`items-report-preset-${option.value}`}
                    aria-pressed={preset === option.value}
                    className={preset === option.value ? `${classes.preset} ${classes.presetActive}` : classes.preset}
                    onClick={() => handlePreset(option.value)}
                  >
                    {option.label}
                  </UnstyledButton>
                ))}
              </div>
              {preset === 'custom' ? (
                <div className={classes.rangeRow}>
                  <TextInput
                    type="date"
                    label="מתאריך"
                    data-testid="items-report-from"
                    value={range.from}
                    onChange={(event) => {
                      const from = event.currentTarget.value;
                      setRange((current) => ({ ...current, from }));
                      setReport(null);
                    }}
                  />
                  <TextInput
                    type="date"
                    label="עד תאריך"
                    data-testid="items-report-to"
                    value={range.to}
                    onChange={(event) => {
                      const to = event.currentTarget.value;
                      setRange((current) => ({ ...current, to }));
                      setReport(null);
                    }}
                  />
                </div>
              ) : (
                <div className={classes.rangeHint} data-testid="items-report-range-hint">
                  {`משמרות שנפתחו ב-${formatRangeLabel(range.from, range.to)}`}
                </div>
              )}
            </section>

            <section className={classes.card}>
              <div className={classes.cardLabel}>היקף הדוח</div>
              <div className={classes.scopeRow}>
                <UnstyledButton
                  component="button"
                  type="button"
                  data-testid="items-report-scope-all"
                  aria-pressed={scope === 'all'}
                  className={scope === 'all' ? `${classes.scope} ${classes.scopeActive}` : classes.scope}
                  onClick={() => handleScope('all')}
                >
                  <FontAwesomeIcon icon={faLayerGroup as IconProp} />
                  <span className={classes.scopeTitle}>כל הפריטים</span>
                </UnstyledButton>
                <UnstyledButton
                  component="button"
                  type="button"
                  data-testid="items-report-scope-specific"
                  aria-pressed={scope === 'specific'}
                  className={scope === 'specific' ? `${classes.scope} ${classes.scopeActive}` : classes.scope}
                  onClick={() => handleScope('specific')}
                >
                  <FontAwesomeIcon icon={faListCheck as IconProp} />
                  <span className={classes.scopeTitle}>פריטים ספציפיים</span>
                </UnstyledButton>
              </div>

              {scope === 'specific' && (
                <UnstyledButton
                  component="button"
                  type="button"
                  data-testid="items-report-open-picker"
                  className={classes.selectTrigger}
                  onClick={() => setPickerOpen(true)}
                >
                  <span>{selectedCount === 0 ? 'בחר פריטים' : `${selectedCount} פריטים נבחרו`}</span>
                  <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                </UnstyledButton>
              )}
            </section>

            <Button
              variant="save"
              fullWidth
              onClick={() => { void runReport(); }}
              disabled={!canRun || loading}
              dataTestId="items-report-run"
            >
              {loading ? 'מפיק...' : 'צפה בדוח'}
            </Button>

            {loading && <div className={classes.center}><Loader /></div>}

            {!loading && report && (
              report.rows.length === 0 ? (
                <div className={classes.empty} data-testid="items-report-empty">אין נתוני מכירות בטווח שנבחר</div>
              ) : (
                <>
                  <section className={classes.tableCard} data-testid="items-report-table">
                    <div className={classes.tableScroll}>
                      <table className={classes.table}>
                        <thead>
                          <tr>
                            <th>שם פריט</th>
                            <th>כמות שנמכרה</th>
                            <th>מחיר ממוצע בפועל</th>
                            <th>סה״כ מכירות</th>
                            <th>% מהמכירות</th>
                            <th>כמות שבוטלה</th>
                          </tr>
                          {totals && (
                            <tr className={classes.totalsRow} data-testid="items-report-totals">
                              <th className={classes.nameCell}>סה״כ</th>
                              <th>{totals.soldQuantity}</th>
                              <th><Money value={totals.avgActualPrice} /></th>
                              <th><Money value={totals.salesAmount} /></th>
                              <th>{`${totals.salesSharePercent}%`}</th>
                              <th>{totals.canceledQuantity}</th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {report.rows.map((row: ItemsReportRow) => {
                            const rowKey = `${row.productId}-${row.variantItemId ?? 'base'}`;
                            return (
                              <React.Fragment key={rowKey}>
                                <tr data-testid={`items-report-row-${rowKey}`}>
                                  <td className={classes.nameCell}>
                                    {row.productName}
                                    {row.variantName && <span className={classes.variantTag}>{row.variantName}</span>}
                                  </td>
                                  <td>{row.soldQuantity}</td>
                                  <td><Money value={row.avgActualPrice} /></td>
                                  <td><Money value={row.salesAmount} /></td>
                                  <td>{`${row.salesSharePercent}%`}</td>
                                  <td className={row.canceledQuantity > 0 ? classes.canceledCell : undefined}>{row.canceledQuantity}</td>
                                </tr>
                                {row.comboItems.map((item, index) => (
                                  <tr
                                    key={`${rowKey}-${item.itemId ?? `name-${index}`}`}
                                    className={classes.comboRow}
                                    data-testid={`items-report-combo-row-${rowKey}-${item.itemId ?? index}`}
                                  >
                                    <td className={classes.comboNameCell}>
                                      <span className={classes.comboArrow} aria-hidden="true">↳</span>
                                      {item.itemName}
                                    </td>
                                    <td>{item.soldQuantity}</td>
                                    <td><Money value={item.avgActualPrice} /></td>
                                    <td>
                                      <span className={classes.comboWithin}>מתוך</span>
                                      <Money value={item.salesAmount} />
                                    </td>
                                    <td className={classes.comboMuted}>—</td>
                                    <td className={item.canceledQuantity > 0 ? classes.canceledCell : undefined}>{item.canceledQuantity}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <div className={classes.cardList} data-testid="items-report-cards">
                    {totals && (
                      <article className={`${classes.itemCard} ${classes.totalsCard}`} data-testid="items-report-totals-card">
                        <div className={classes.cardTitle}>סה״כ</div>
                        <div className={classes.cardCells}>
                          <Cell label="כמות שנמכרה" value={totals.soldQuantity} />
                          <Cell label="מחיר ממוצע בפועל" value={<Money value={totals.avgActualPrice} />} />
                          <Cell label="סה״כ מכירות" value={<Money value={totals.salesAmount} />} />
                          <Cell label="% מהמכירות" value={`${totals.salesSharePercent}%`} />
                          <Cell label="כמות שבוטלה" value={totals.canceledQuantity} />
                        </div>
                      </article>
                    )}

                    {report.rows.map((row: ItemsReportRow) => {
                      const rowKey = `${row.productId}-${row.variantItemId ?? 'base'}`;
                      return (
                        <article key={rowKey} className={classes.itemCard} data-testid={`items-report-card-${rowKey}`}>
                          <div className={classes.cardTitle}>
                            {row.productName}
                            {row.variantName && <span className={classes.variantTag}>{row.variantName}</span>}
                          </div>
                          <div className={classes.cardCells}>
                            <Cell label="כמות שנמכרה" value={row.soldQuantity} />
                            <Cell label="מחיר ממוצע בפועל" value={<Money value={row.avgActualPrice} />} />
                            <Cell label="סה״כ מכירות" value={<Money value={row.salesAmount} />} />
                            <Cell label="% מהמכירות" value={`${row.salesSharePercent}%`} />
                            <Cell
                              label="כמות שבוטלה"
                              value={row.canceledQuantity}
                              tone={row.canceledQuantity > 0 ? 'danger' : undefined}
                            />
                          </div>
                          {row.comboItems.length > 0 && (
                            <div className={classes.cardCombos}>
                              {row.comboItems.map((item, index) => (
                                <div key={`${rowKey}-${item.itemId ?? `name-${index}`}`} className={classes.cardComboRow}>
                                  <span className={classes.cardComboName}>
                                    <span className={classes.comboArrow} aria-hidden="true">↳</span>
                                    {item.itemName}
                                  </span>
                                  <span className={classes.cardComboMeta}>
                                    <Money value={item.salesAmount} />
                                    <span className={classes.comboWithin}>מתוך</span>
                                    {item.soldQuantity}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>

      <PosItemsProductPickerModal
        opened={pickerOpen}
        products={products}
        optionItems={optionItems}
        selectedProductIds={selectedIds}
        selectedOptionItemIds={selectedOptionItemIds}
        onClose={() => setPickerOpen(false)}
        onConfirm={(productIds, optionIds) => {
          setSelectedIds(productIds);
          setSelectedOptionItemIds(optionIds);
          setReport(null);
          setPickerOpen(false);
        }}
      />
    </Modal>
  );
};

export const PosItemsReportModal = React.memo(PosItemsReportModalComponent);
